import { useId, useState, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@jbnl/shared-ui";
import {
  isLeeg,
  telActief,
  telling,
  type Filterdescriptor,
  type Filteroptie,
  type Filterstand,
  type Filterwaarde,
} from "./data-table-filter";

/** Above this many options a filter grows a search box of its own: korps has 154. */
const ZOEKDREMPEL = 10;

/** How many options stand open before the rest are behind "nog … tonen". */
const GETOOND = 6;

/** One button per filter, in a row above the table, each opening a popover of its own controls. */
export function Filterbalk<TData>({
  descriptors,
  rijen,
  stand,
  onStand,
  children,
}: {
  descriptors: Filterdescriptor<TData>[];
  rijen: TData[];
  stand: Filterstand;
  onStand: (stand: Filterstand) => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  const actief = telActief(descriptors, stand) > 0;
  // A popover that closes for real gets its focus back on the trigger; one that closes because
  // a different filter opened does not - see `onCloseAutoFocus` below for why.
  const teruggevenAanTrigger = open === null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {descriptors.map((descriptor) => (
          <FilterKnop
            key={descriptor.id}
            descriptor={descriptor}
            descriptors={descriptors}
            rijen={rijen}
            stand={stand}
            onStand={onStand}
            open={open === descriptor.id}
            teruggevenAanTrigger={teruggevenAanTrigger}
            // B's open and A's outside-pointerdown close race in an order Radix does not
            // guarantee, so A's close only clears the id if it is still the one open.
            onOpenChange={(volgende) =>
              setOpen((huidig) => {
                if (volgende) return descriptor.id;
                return huidig === descriptor.id ? null : huidig;
              })
            }
          />
        ))}

        {actief && (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0"
            onClick={() => onStand({})}
          >
            {t("table.filters.wissen")}
          </Button>
        )}
      </div>

      {children}
    </div>
  );
}

function FilterKnop<TData>({
  descriptor,
  descriptors,
  rijen,
  stand,
  onStand,
  open,
  teruggevenAanTrigger,
  onOpenChange,
}: {
  descriptor: Filterdescriptor<TData>;
  descriptors: Filterdescriptor<TData>[];
  rijen: TData[];
  stand: Filterstand;
  onStand: (stand: Filterstand) => void;
  open: boolean;
  teruggevenAanTrigger: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const waarde = stand[descriptor.id];
  const aan = !isLeeg(waarde);
  // With the panel closed by default, the button is the only place this can say so.
  const opStandaard =
    descriptor.standaard !== undefined &&
    JSON.stringify(waarde) === JSON.stringify(descriptor.standaard);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant={aan ? "secondary" : "outline"}
          size="sm"
          aria-expanded={open}
        >
          <span className={cn(aan && "font-bold")}>
            {aan ? knopTekst(descriptor, waarde!, t) : descriptor.label}
          </span>
          {opStandaard && (
            <span className="text-xs font-normal text-muted-foreground">
              {t("table.filters.standaard")}
            </span>
          )}
          <ChevronDown aria-hidden />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        // Radix always returns focus to this trigger on unmount, and that bounce is what closes
        // a *different* trigger's popover the moment it opens - only take it back when it's ours.
        onCloseAutoFocus={(event) => {
          if (!teruggevenAanTrigger) event.preventDefault();
        }}
      >
        <div className="space-y-2 text-sm">
          <p className="font-medium">{descriptor.label}</p>

          <Facet
            descriptor={descriptor}
            descriptors={descriptors}
            rijen={rijen}
            stand={stand}
            onZet={(nieuw) => onStand({ ...stand, [descriptor.id]: nieuw })}
          />

          <div className="flex items-center justify-between pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-0"
              disabled={!aan}
              onClick={() => onStand({ ...stand, [descriptor.id]: undefined })}
            >
              {t("table.filters.wissen")}
            </Button>
            <Button size="sm" onClick={() => onOpenChange(false)}>
              {t("table.filters.toepassen")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * What a closed button says once its filter is on - `zegt`, or a fallback built from the value
 * itself. Every screen in the app leaves `zegt` out today, so the fallback is not an edge case.
 */
function knopTekst<TData>(
  descriptor: Filterdescriptor<TData>,
  waarde: Filterwaarde,
  t: TFunction,
): string {
  if (waarde.soort === "schakelaar")
    return descriptor.zegt?.(waarde) ?? descriptor.label;

  const inhoud = descriptor.zegt
    ? descriptor.zegt(waarde)
    : standaardInhoud(descriptor, waarde, t);
  return `${descriptor.label}: ${inhoud}`;
}

function standaardInhoud<TData>(
  descriptor: Filterdescriptor<TData>,
  waarde: Filterwaarde,
  t: TFunction,
): string {
  switch (waarde.soort) {
    case "keuze":
      return (
        descriptor.opties?.find((o) => o.waarde === waarde.waarde)?.label ??
        waarde.waarde
      );
    case "meerkeuze":
      if (waarde.waarden.length === 1) {
        return (
          descriptor.opties?.find((o) => o.waarde === waarde.waarden[0])
            ?.label ?? waarde.waarden[0]
        );
      }
      return t("table.filters.aantalGekozen", { count: waarde.waarden.length });
    case "periode":
      return [waarde.van, waarde.tot].filter(Boolean).join(" - ");
    case "schakelaar":
      return descriptor.label;
  }
}

/** Which kind of control a descriptor draws; `keuze` and `meerkeuze` share `LijstFacet`. */
function Facet<TData>({
  descriptor,
  descriptors,
  rijen,
  stand,
  onZet,
}: {
  descriptor: Filterdescriptor<TData>;
  descriptors: Filterdescriptor<TData>[];
  rijen: TData[];
  stand: Filterstand;
  onZet: (waarde: Filterwaarde) => void;
}) {
  const waarde = stand[descriptor.id];

  if (descriptor.kind === "schakelaar") {
    return <SchakelaarFacet waarde={waarde} onZet={onZet} />;
  }

  if (descriptor.kind === "periode") {
    return (
      <PeriodeFacet descriptor={descriptor} waarde={waarde} onZet={onZet} />
    );
  }

  return (
    <LijstFacet
      descriptor={descriptor}
      descriptors={descriptors}
      rijen={rijen}
      stand={stand}
      onZet={onZet}
    />
  );
}

function SchakelaarFacet({
  waarde,
  onZet,
}: {
  waarde: Filterwaarde | undefined;
  onZet: (waarde: Filterwaarde) => void;
}) {
  const { t } = useTranslation();
  const id = useId();

  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={id}
        checked={waarde?.soort === "schakelaar" && waarde.aan}
        onCheckedChange={(aan) =>
          onZet({ soort: "schakelaar", aan: aan === true })
        }
      />
      <Label htmlFor={id} className="font-normal">
        {t("table.filters.ja")}
      </Label>
    </div>
  );
}

function PeriodeFacet<TData>({
  descriptor,
  waarde,
  onZet,
}: {
  descriptor: Filterdescriptor<TData>;
  waarde: Filterwaarde | undefined;
  onZet: (waarde: Filterwaarde) => void;
}) {
  const { t } = useTranslation();
  const periode = waarde?.soort === "periode" ? waarde : { van: "", tot: "" };

  return (
    <div className="grid gap-1.5">
      <Input
        type="date"
        aria-label={t("table.filters.van", { label: descriptor.label })}
        value={periode.van}
        onChange={(e) =>
          onZet({ soort: "periode", van: e.target.value, tot: periode.tot })
        }
      />
      <Input
        type="date"
        aria-label={t("table.filters.tot", { label: descriptor.label })}
        value={periode.tot}
        onChange={(e) =>
          onZet({ soort: "periode", van: periode.van, tot: e.target.value })
        }
      />
    </div>
  );
}

/**
 * `keuze` and `meerkeuze` draw the same list of vinkjes: the difference between them is whether
 * the previous one goes off, which is not something a control should look different for.
 */
function LijstFacet<TData>({
  descriptor,
  descriptors,
  rijen,
  stand,
  onZet,
}: {
  descriptor: Filterdescriptor<TData>;
  descriptors: Filterdescriptor<TData>[];
  rijen: TData[];
  stand: Filterstand;
  onZet: (waarde: Filterwaarde) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [alles, setAlles] = useState(false);
  const [term, setTerm] = useState("");
  const waarde = stand[descriptor.id];

  // The label heads the facet, so it is capitalised; inside "zoek een …" it is a noun.
  const zoekLabel = t("table.filters.zoek", {
    label: descriptor.label.toLowerCase(),
  });

  const alleOpties = descriptor.opties ?? [];
  const gezocht = term
    ? alleOpties.filter((o) =>
        o.label.toLowerCase().includes(term.toLowerCase()),
      )
    : alleOpties;
  const opties = alles ? gezocht : gezocht.slice(0, GETOOND);
  const gekozen =
    waarde?.soort === "meerkeuze"
      ? waarde.waarden
      : waarde?.soort === "keuze" && waarde.waarde
        ? [waarde.waarde]
        : [];

  return (
    <>
      {alleOpties.length > ZOEKDREMPEL && (
        <Input
          type="search"
          aria-label={zoekLabel}
          placeholder={zoekLabel}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
      )}

      <ul className="max-h-64 space-y-1 overflow-y-auto">
        {opties.map((optie) => (
          <FacetOptie
            key={optie.waarde}
            id={id}
            optie={optie}
            descriptor={descriptor}
            aantal={telling(
              rijen,
              descriptors,
              stand,
              descriptor,
              optie.waarde,
            )}
            gekozen={gekozen}
            onZet={onZet}
          />
        ))}
      </ul>

      {gezocht.length > GETOOND && (
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => setAlles(!alles)}
        >
          {alles
            ? t("table.filters.minder")
            : t("table.filters.meer", { count: gezocht.length - GETOOND })}
        </Button>
      )}
    </>
  );
}

function FacetOptie<TData>({
  id,
  optie,
  descriptor,
  aantal,
  gekozen,
  onZet,
}: {
  id: string;
  optie: Filteroptie;
  descriptor: Filterdescriptor<TData>;
  aantal: number;
  gekozen: string[];
  onZet: (waarde: Filterwaarde) => void;
}) {
  const vink = gekozen.includes(optie.waarde);

  return (
    <li className="flex items-center gap-2">
      {/* An option that would leave nothing goes flat rather than disappearing: a list
          that loses entries as you tick sends a beheerder hunting for one still there. */}
      <Checkbox
        id={`${id}-${optie.waarde}`}
        checked={vink}
        disabled={Boolean(descriptor.past) && aantal === 0 && !vink}
        onCheckedChange={(aan) =>
          onZet(
            descriptor.kind === "keuze"
              ? { soort: "keuze", waarde: aan ? optie.waarde : "" }
              : {
                  soort: "meerkeuze",
                  waarden: aan
                    ? [...gekozen, optie.waarde]
                    : gekozen.filter((w) => w !== optie.waarde),
                },
          )
        }
      />
      <Label
        htmlFor={`${id}-${optie.waarde}`}
        className="min-w-0 flex-1 truncate font-normal"
      >
        {optie.label}
      </Label>
      {/* Only where the rows are held whole - see `telling`. */}
      {descriptor.past && (
        <span className="text-xs text-muted-foreground tabular-nums">
          {aantal}
        </span>
      )}
    </li>
  );
}
